import React from "react";
import { Link } from "react-router-dom";
import Layout from "../layouts/Default";
import EditBar from "../parts/EditBar";
import Icon from "@mdi/react";
import { mdiMapMarker, mdiAccountSupervisor } from "@mdi/js";
import Avatar from "../components/avatar/Avatar";
import Text from "../components/typography/Text";
import fetchApi from "../fetchApi";
import AuthContext from "../contexts/auth";
import Links from "../components/links/Links";
import Content from "../parts/Project/Content";
import useMedia from "../hooks/useMedia";
import Button from "../components/buttons/Button";
import NotFound from "./404";
import Loader from "../components/loaders/Loader";

const Project = ({ location, match, history }) => {
  const { user } = React.useContext(AuthContext);
  const { slug } = match.params;
  const media = useMedia();
  const [showDetails, toggleDetails] = React.useReducer(s => !s, false);
  const loggedUser = user ? user.slug === slug : false;
  const [loading, setLoading] = React.useState(true);
  const [project, setProject] = React.useState(
    location.state ? location.state.project : null
  );
  const [missions, setMissions] = React.useState([]);
  const openedMissionsCount = missions
    ? missions.filter(({ close }) => !close).length
    : 0;
  const canApply =
    !loggedUser &&
    user &&
    user.role !== "contractor" &&
    openedMissionsCount > 0;

  const fetchProject = React.useMemo(
    () => async () => {
      setLoading(true);
      try {
        const [fetchedProject] = await fetchApi(`/users?slug=${slug}`);
        setProject(fetchedProject);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [slug]
  );

  const notice =
    location.state && history.action === "PUSH" ? location.state.notice : false;

  React.useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const fetchMissions = async () => {
    if (!project) return;
    try {
      const fetchedMissions = await fetchApi(`/mission?author=${project.id}`);
      setMissions(fetchedMissions);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    fetchMissions();
  }, [project]); // eslint-disable-line

  if (loading)
    return (
      <Layout title="Loading...">
        <div style={{ marginBottom: "60px" }} />
        <Loader />
      </Layout>
    );
  else if (!slug || !project) return <NotFound />;

  return (
    <Layout
      title={project.company}
      topBar={
        loggedUser ? (
          <EditBar
            actionLabel="Post design job"
            actionLink="/project/editmission"
          />
        ) : null
      }
    >
      <div className="pProject">
        <div className={`pProject_profile ${loggedUser ? "-logged" : ""}`}>
          <div className="pProject_part -header">
            <div className="pProject_picture">
              <Avatar avatar={project.avatar} />
            </div>
            <div className="pProject_name">
              <h4>{project.company}</h4>
            </div>
            {project?.ecosystem && (
              <div className="pProject_ecosystem">{project.ecosystem}</div>
            )}

            <div className="pProject_buttons">
              {media === "mobile" && (
                <Button outlined onClick={toggleDetails} rounded>
                  Show details
                </Button>
              )}
              {canApply && (
                <Link
                  to={{
                    pathname: "/project/workwith",
                    state: { user: project },
                  }}
                >
                  <Button black icon="send" rounded>
                    Apply
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {(media !== "mobile" || showDetails) && (
            <div className="pProject_details">
              <div className="pProject_part -contact">
                <Text small>Contact person</Text>
                <h5>
                  {project.contact_name}, {project.contact_job}
                </h5>
              </div>
              <div className="pProject_part">
                <h5>
                  <Icon path={mdiMapMarker} size="19px" />
                  {project.city}, {project.country}
                </h5>
                <h5>
                  <Icon path={mdiAccountSupervisor} size="19px" />
                  {project.company_size} people
                </h5>
              </div>
              <div className="pProject_part">
                <Links {...project} />
              </div>
            </div>
          )}
        </div>
        <div className="pProject_content">
          <Content
            {...project}
            notice={notice}
            missions={missions}
            fetchMissions={fetchMissions}
            canApply={canApply}
            loggedUser={loggedUser}
          />
        </div>
      </div>
    </Layout>
  );
};

export default Project;
